import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MerchantRecommendationsResponse,
  MerchantRecommendedCreator,
  NbaAction,
  RecommendationsCountResponse,
  RecommendationsResponse,
  RecommendedCreator,
} from "@unbox/api";
import { RECOMMENDATIONS_NEGATABLE_FILTERS } from "@unbox/api";
import { useCallback } from "react";
import { buildCandidateAttributeQuery, type CandidateAttributeFilters } from "@/api/candidates";
import { handleError } from "@/api/error";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { toNotQuery } from "@/providers/filter-registry";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client, type MerchantScope, useScopedMerchantClient } from "./scoped-merchant-client";

export type {
  MerchantRecommendationsResponse,
  MerchantRecommendedCreator,
  NbaAction,
  RecommendationsCountResponse,
  RecommendationsResponse,
  RecommendedCreator,
};

const withUniqueRecommendations = <T extends { recommendations: { tiktokCreatorOpenId: string }[] }>(
  response: T,
): T => {
  const seen = new Set<string>();
  const recommendations = response.recommendations.filter((r) => {
    if (seen.has(r.tiktokCreatorOpenId)) return false;
    seen.add(r.tiktokCreatorOpenId);
    return true;
  });
  return { ...response, recommendations };
};

const collaborationRecommendationsKey = (
  scope: MerchantScope,
  collaborationId: string | undefined,
  pagination: { limit: number; offset: number },
) => [
  "collaboration-recommendations",
  scope.merchantId,
  scope.workspaceId,
  collaborationId,
  pagination.limit,
  pagination.offset,
];

const fetchCollaborationRecommendations = async (
  scope: MerchantScope,
  collaborationId: string,
  pagination: { limit: number; offset: number },
): Promise<RecommendationsResponse> => {
  const query = {
    limit: String(pagination.limit),
    offset: String(pagination.offset),
  };

  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
        ":collaborationId"
      ].recommendations.$get({
        param: { ...scope.param, collaborationId },
        query,
      })
    : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].recommendations.$get({
        param: { ...scope.param, collaborationId },
        query,
      });

  if (!res.ok) {
    throw await handleError(res);
  }

  return withUniqueRecommendations(await res.json());
};

export const useCollaborationRecommendations = (
  collaborationId: string | undefined,
  options: {
    pagination: { limit: number; offset: number };
  },
) => {
  const scope = useScopedMerchantClient();
  const { pagination } = options;

  return useQuery({
    queryKey: collaborationRecommendationsKey(scope, collaborationId, pagination),
    queryFn: async (): Promise<RecommendationsResponse> => {
      if (!scope.merchantId || !collaborationId) {
        return { recommendations: [], total: 0, hasUnactionedRecommendations: false };
      }

      return await fetchCollaborationRecommendations(scope, collaborationId, pagination);
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

export const useCollaborationRecommendationRowsFetcher = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useCallback(
    async (offset: number, limit: number): Promise<ChunkResponse<RecommendedCreator>> => {
      if (!collaborationId) return { items: [], total: 0 };
      const pagination = { limit, offset };
      const data = await queryClient.fetchQuery({
        queryKey: collaborationRecommendationsKey(scope, collaborationId, pagination),
        queryFn: () => fetchCollaborationRecommendations(scope, collaborationId, pagination),
        staleTime: Number.POSITIVE_INFINITY,
      });
      return { items: data.recommendations, total: data.total };
    },
    [scope, collaborationId],
  );
};

export const useRecommendationsCount = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["recommendations-count", scope.merchantId, scope.workspaceId, collaborationId],
    queryFn: async (): Promise<RecommendationsCountResponse> => {
      if (!scope.merchantId || !collaborationId) {
        return { total: 0, hasUnactionedRecommendations: false };
      }

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
            ":collaborationId"
          ].recommendations.count.$get({
            param: { ...scope.param, collaborationId },
          })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].recommendations.count.$get({
            param: { ...scope.param, collaborationId },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId && !!collaborationId,
  });
};

// ─── Optimistic update helpers ───────────────────────────────────────────────
//
// We deliberately use setQueriesData (optimistic update) instead of
// invalidateQueries here. The designer requires strict batch behaviour:
// the list shrinks as creators are passed/invited and must NOT auto-backfill
// until the user explicitly reloads the page or navigates away. Using
// invalidateQueries would trigger a refetch that repopulates the list to 400,
// defeating the batch UX entirely.
//
// Each helper only decrements totals when an item was actually removed,
// preventing count drift if the same creator is actioned more than once.

function filterRecommendation(oldData: RecommendationsResponse, tiktokCreatorOpenId: string): RecommendationsResponse {
  const filtered = oldData.recommendations.filter((r) => r.tiktokCreatorOpenId !== tiktokCreatorOpenId);
  const removed = oldData.recommendations.length - filtered.length;
  return {
    ...oldData,
    recommendations: filtered,
    total: removed > 0 ? oldData.total - removed : oldData.total,
    hasUnactionedRecommendations: filtered.length > 0,
  };
}

// Helper: build an updated MerchantRecommendationsResponse after removing one creator.
function filterMerchantRecommendation(
  oldData: MerchantRecommendationsResponse,
  tiktokCreatorOpenId: string,
): MerchantRecommendationsResponse {
  const filtered = oldData.recommendations.filter((r) => r.tiktokCreatorOpenId !== tiktokCreatorOpenId);
  const removed = oldData.recommendations.length - filtered.length;
  return {
    ...oldData,
    recommendations: filtered,
    total: removed > 0 ? oldData.total - removed : oldData.total,
    hasUnactionedRecommendations: filtered.length > 0,
  };
}

// Helper: decrement count cache only when something was actually removed.
function decrementCountCache(oldData: RecommendationsCountResponse, removed: number): RecommendationsCountResponse {
  const newTotal = removed > 0 ? Math.max(0, oldData.total - removed) : oldData.total;
  return { total: newTotal, hasUnactionedRecommendations: newTotal > 0 };
}

export const usePassRecommendation = (collaborationId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: { tiktokCreatorOpenId: string; modelVersion: string; action: NbaAction }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
            ":collaborationId"
          ].recommendations.pass.$post({
            param: { ...scope.param, collaborationId },
            json: payload,
          })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].recommendations.pass.$post({
            param: { ...scope.param, collaborationId },
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: (_data, variables) => {
      if (scope.merchantId) {
        let removed = 0;
        queryClient.setQueriesData<RecommendationsResponse>(
          { queryKey: ["collaboration-recommendations", scope.merchantId, scope.workspaceId, collaborationId] },
          (oldData) => {
            if (!oldData) return oldData;
            const next = filterRecommendation(oldData, variables.tiktokCreatorOpenId);
            removed += oldData.recommendations.length - next.recommendations.length;
            return next;
          },
        );
        queryClient.setQueriesData<RecommendationsCountResponse>(
          { queryKey: ["recommendations-count", scope.merchantId, scope.workspaceId, collaborationId] },
          (oldData) => (oldData ? decrementCountCache(oldData, removed) : oldData),
        );
      }
    },
  });
};

/**
 * Optimistic-only cache update for invite actions. Use after a successful bulk-add-creators call —
 * the server now records the NBA interaction itself (via the workflow / sync route paths), so the
 * FE no longer needs to fire per-creator POSTs to /recommendations/invite. This hook keeps the
 * recommendations list/count cache feeling responsive without making any HTTP call of its own.
 */
export const useOptimisticallyRemoveCollaborationRecommendations = (collaborationId: string | undefined) => {
  const scope = useScopedMerchantClient();
  return (
    openIds: string[],
    options: { invalidateCollaborationCreators?: boolean } = { invalidateCollaborationCreators: true },
  ) => {
    if (!scope.merchantId || !collaborationId || openIds.length === 0) return;
    let removed = 0;
    queryClient.setQueriesData<RecommendationsResponse>(
      { queryKey: ["collaboration-recommendations", scope.merchantId, scope.workspaceId, collaborationId] },
      (oldData) => {
        if (!oldData) return oldData;
        let next = oldData;
        for (const openId of openIds) {
          const after = filterRecommendation(next, openId);
          removed += next.recommendations.length - after.recommendations.length;
          next = after;
        }
        return next;
      },
    );
    queryClient.setQueriesData<RecommendationsCountResponse>(
      { queryKey: ["recommendations-count", scope.merchantId, scope.workspaceId, collaborationId] },
      (oldData) => (oldData ? decrementCountCache(oldData, removed) : oldData),
    );
    queryClient.invalidateQueries({ queryKey: ["target-collaborations", scope.merchantId, scope.workspaceId] });
    if (options.invalidateCollaborationCreators) {
      queryClient.invalidateQueries({
        queryKey: ["target-collaboration-creators", scope.merchantId, scope.workspaceId, collaborationId],
      });
    }
  };
};

export const useRecordInviteAction = (collaborationId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({
      collaborationIdOverride,
      ...payload
    }: {
      tiktokCreatorOpenId: string;
      modelVersion: string;
      action: NbaAction;
      // Used when a draft collab was just published — the URL's `collaborationId` is the
      // now-defunct draft id, and the API call must target the freshly issued real id.
      collaborationIdOverride?: string;
    }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
            ":collaborationId"
          ].recommendations.invite.$post({
            param: { ...scope.param, collaborationId: collaborationIdOverride ?? collaborationId },
            json: payload,
          })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].recommendations.invite.$post({
            param: { ...scope.param, collaborationId: collaborationIdOverride ?? collaborationId },
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: (_data, variables) => {
      if (scope.merchantId) {
        let removed = 0;
        queryClient.setQueriesData<RecommendationsResponse>(
          { queryKey: ["collaboration-recommendations", scope.merchantId, scope.workspaceId, collaborationId] },
          (oldData) => {
            if (!oldData) return oldData;
            const next = filterRecommendation(oldData, variables.tiktokCreatorOpenId);
            removed += oldData.recommendations.length - next.recommendations.length;
            return next;
          },
        );
        queryClient.setQueriesData<RecommendationsCountResponse>(
          { queryKey: ["recommendations-count", scope.merchantId, scope.workspaceId, collaborationId] },
          (oldData) => (oldData ? decrementCountCache(oldData, removed) : oldData),
        );
        queryClient.invalidateQueries({ queryKey: ["target-collaborations", scope.merchantId, scope.workspaceId] });
        queryClient.invalidateQueries({
          queryKey: ["target-collaboration-creators", scope.merchantId, scope.workspaceId, collaborationId],
        });
      }
    },
  });
};

/**
 * Records an invite action for every collaboration in a Smart Group simultaneously.
 * Falls back to the single-collaboration behavior when only one ID is provided.
 */
export const useRecordInviteActionForGroup = (collaborationIds: string[]) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: { tiktokCreatorOpenId: string; modelVersion: string; action: NbaAction }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      await Promise.all(
        collaborationIds.map((collaborationId) =>
          (scope.workspaceId
            ? client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
                ":collaborationId"
              ].recommendations.invite.$post({
                param: { ...scope.param, collaborationId },
                json: payload,
              })
            : client.merchant[":merchantId"].collaborations.target[":collaborationId"].recommendations.invite.$post({
                param: { ...scope.param, collaborationId },
                json: payload,
              })
          ).then((res) => (res.ok ? res.json() : handleError(res).then((e) => Promise.reject(e)))),
        ),
      );
    },
    onSuccess: (_data, variables) => {
      if (scope.merchantId) {
        for (const collaborationId of collaborationIds) {
          let removed = 0;
          queryClient.setQueriesData<RecommendationsResponse>(
            { queryKey: ["collaboration-recommendations", scope.merchantId, scope.workspaceId, collaborationId] },
            (oldData) => {
              if (!oldData) return oldData;
              const next = filterRecommendation(oldData, variables.tiktokCreatorOpenId);
              removed += oldData.recommendations.length - next.recommendations.length;
              return next;
            },
          );
          queryClient.setQueriesData<RecommendationsCountResponse>(
            { queryKey: ["recommendations-count", scope.merchantId, scope.workspaceId, collaborationId] },
            (oldData) => (oldData ? decrementCountCache(oldData, removed) : oldData),
          );
          queryClient.invalidateQueries({ queryKey: ["target-collaborations", scope.merchantId, scope.workspaceId] });
          queryClient.invalidateQueries({
            queryKey: ["target-collaboration-creators", scope.merchantId, scope.workspaceId, collaborationId],
          });
        }
      }
    },
  });
};

export const useRecordFetchAction = (collaborationId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: { tiktokCreatorOpenId: string; modelVersion: string; action: NbaAction }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].collaborations.target[
            ":collaborationId"
          ].recommendations.fetch.$post({
            param: { ...scope.param, collaborationId },
            json: payload,
          })
        : await client.merchant[":merchantId"].collaborations.target[":collaborationId"].recommendations.fetch.$post({
            param: { ...scope.param, collaborationId },
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    // No query invalidation needed — viewing doesn't change what's shown
  });
};

// =============================================================================
// Merchant-level recommendations (for Explorer pages)
// =============================================================================

export type RecommendationsFilters = CandidateAttributeFilters & {
  age?: ("18-24" | "25-34" | "35-44" | "45-54" | "55+")[];
  includeTagIds?: string[];
  excludeTagIds?: string[];
  includeCohortIds?: string[];
  excludeCohortIds?: string[];
};

const buildRecommendationsFilterQuery = (filters: RecommendationsFilters = {}) => ({
  ...buildCandidateAttributeQuery(filters),
  ...(filters.age?.length && { age: filters.age }),
  ...(filters.includeTagIds?.length && { includeTagIds: filters.includeTagIds }),
  ...(filters.excludeTagIds?.length && { excludeTagIds: filters.excludeTagIds }),
  ...(filters.includeCohortIds?.length && { includeCohortIds: filters.includeCohortIds }),
  ...(filters.excludeCohortIds?.length && { excludeCohortIds: filters.excludeCohortIds }),
  not: toNotQuery(filters.not, RECOMMENDATIONS_NEGATABLE_FILTERS),
});

type MerchantRecommendationsQuery = {
  pagination: { limit: number; offset: number };
  isCreator?: boolean;
  allCreators?: boolean;
  filters?: RecommendationsFilters;
};

const fetchMerchantRecommendations = async (
  scope: MerchantScope,
  { pagination, isCreator, allCreators, filters }: MerchantRecommendationsQuery,
): Promise<MerchantRecommendationsResponse> => {
  const query = {
    limit: String(pagination.limit),
    offset: String(pagination.offset),
    ...(isCreator !== undefined && { isCreator: String(isCreator) }),
    ...(allCreators !== undefined && { allCreators: String(allCreators) }),
    ...buildRecommendationsFilterQuery(filters),
  };

  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].recommendations.$get({
        param: scope.param,
        query,
      })
    : await client.merchant[":merchantId"].recommendations.$get({ param: scope.param, query });

  if (!res.ok) {
    throw await handleError(res);
  }

  return withUniqueRecommendations(await res.json());
};

export const useMerchantRecommendations = (options: {
  pagination: { limit: number; offset: number };
  isCreator?: boolean;
  // Additive — ignores isCreator entirely and returns creators + candidates together.
  // Omit/false preserves the exact current isCreator-only behavior.
  allCreators?: boolean;
  enabled?: boolean;
  filters?: RecommendationsFilters;
}) => {
  const scope = useScopedMerchantClient();
  const { pagination, isCreator, allCreators, enabled = true, filters } = options;

  return useQuery({
    queryKey: [
      "merchant-recommendations",
      scope.merchantId,
      scope.workspaceId,
      isCreator,
      allCreators,
      pagination.limit,
      pagination.offset,
      filters,
    ],
    queryFn: async (): Promise<MerchantRecommendationsResponse> => {
      if (!scope.merchantId) {
        return { recommendations: [], total: 0, hasUnactionedRecommendations: false };
      }

      return await fetchMerchantRecommendations(scope, { pagination, isCreator, allCreators, filters });
    },
    enabled: !!scope.merchantId && enabled,
  });
};

export const useMerchantRecommendationRowsFetcher = ({
  isCreator,
  filters,
}: {
  isCreator: boolean;
  filters?: RecommendationsFilters;
}) => {
  const scope = useScopedMerchantClient();

  return useCallback(
    async (offset: number, limit: number): Promise<ChunkResponse<MerchantRecommendedCreator>> => {
      const pagination = { limit, offset };
      const data = await queryClient.fetchQuery({
        queryKey: [
          "merchant-recommendations",
          scope.merchantId,
          scope.workspaceId,
          isCreator,
          undefined,
          limit,
          offset,
          filters,
        ],
        queryFn: () => fetchMerchantRecommendations(scope, { pagination, isCreator, filters }),
        staleTime: Number.POSITIVE_INFINITY,
      });
      return { items: data.recommendations, total: data.total };
    },
    [scope, isCreator, filters],
  );
};

const RECOMMENDATIONS_CHUNK_SIZE = 200;

export const useMerchantRecommendationsDownload = () => {
  const scope = useScopedMerchantClient();

  return useMutation<
    MerchantRecommendedCreator[],
    Error,
    { isCreator?: boolean; allCreators?: boolean; filters?: RecommendationsFilters }
  >({
    mutationFn: async ({ isCreator, allCreators, filters }) => {
      if (!scope.merchantId) return [];

      const limit = RECOMMENDATIONS_CHUNK_SIZE;
      const fetchChunk = (offset: number) =>
        fetchMerchantRecommendations(scope, { pagination: { limit, offset }, isCreator, allCreators, filters });

      const first = await fetchChunk(0);
      const remaining = Math.max(0, Math.ceil(first.total / limit) - 1);
      const rest = await Promise.all([...Array(remaining).keys()].map((chunk) => fetchChunk(limit * (chunk + 1))));

      return [first, ...rest].flatMap((res) => res.recommendations);
    },
  });
};

export const useMerchantRecommendationsCount = (options: {
  isCreator?: boolean;
  allCreators?: boolean;
  enabled?: boolean;
  filters?: RecommendationsFilters;
}) => {
  const scope = useScopedMerchantClient();
  const { isCreator, allCreators, enabled = true, filters } = options;

  return useQuery({
    queryKey: ["merchant-recommendations-count", scope.merchantId, scope.workspaceId, isCreator, allCreators, filters],
    queryFn: async (): Promise<RecommendationsCountResponse> => {
      if (!scope.merchantId) {
        return { total: 0, hasUnactionedRecommendations: false };
      }

      const filterQuery = buildRecommendationsFilterQuery(filters);

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].recommendations.count.$get({
            param: scope.param,
            query: {
              ...(isCreator !== undefined && { isCreator: String(isCreator) }),
              ...(allCreators !== undefined && { allCreators: String(allCreators) }),
              ...filterQuery,
            },
          })
        : await client.merchant[":merchantId"].recommendations.count.$get({
            param: scope.param,
            query: {
              ...(isCreator !== undefined && { isCreator: String(isCreator) }),
              ...(allCreators !== undefined && { allCreators: String(allCreators) }),
              ...filterQuery,
            },
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    enabled: !!scope.merchantId && enabled,
  });
};

export const usePassMerchantRecommendation = (isCreator?: boolean) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: { tiktokCreatorOpenId: string; modelVersion: string; action: NbaAction }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].recommendations.pass.$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"].recommendations.pass.$post({
            param: scope.param,
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: (_data, variables) => {
      if (scope.merchantId) {
        // Include isCreator in queryKey to only update the active explorer tab
        let removed = 0;
        queryClient.setQueriesData<MerchantRecommendationsResponse>(
          { queryKey: ["merchant-recommendations", scope.merchantId, scope.workspaceId, isCreator] },
          (oldData) => {
            if (!oldData) return oldData;
            const next = filterMerchantRecommendation(oldData, variables.tiktokCreatorOpenId);
            removed = oldData.recommendations.length - next.recommendations.length;
            return next;
          },
        );
        queryClient.setQueriesData<RecommendationsCountResponse>(
          { queryKey: ["merchant-recommendations-count", scope.merchantId, scope.workspaceId, isCreator] },
          (oldData) => (oldData ? decrementCountCache(oldData, removed) : oldData),
        );
      }
    },
  });
};

export const useImportRecommendationsPreview = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (csv: string) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].recommendations.import.preview.$post({
            param: scope.param,
            json: { csv },
          })
        : await client.merchant[":merchantId"].recommendations.import.preview.$post({
            param: scope.param,
            json: { csv },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

export const useDownloadRecommendationsCsvTemplate = () => {
  const scope = useScopedMerchantClient();

  return async () => {
    const res = scope.workspaceId
      ? await client.workspace[":workspaceId"].merchant[":merchantId"].recommendations.import.template.$get({
          param: scope.param,
        })
      : await client.merchant[":merchantId"].recommendations.import.template.$get({ param: scope.param });
    if (!res.ok) throw await handleError(res);

    const blob = await res.blob();
    const filename =
      res.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ?? "invite-creators-template.csv";
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
};

export const useRecordMerchantInviteAction = (isCreator?: boolean) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: { tiktokCreatorOpenId: string; modelVersion: string; action: NbaAction }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].recommendations.invite.$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"].recommendations.invite.$post({
            param: scope.param,
            json: payload,
          });

      if (!res.ok) {
        throw await handleError(res);
      }

      return await res.json();
    },
    onSuccess: (_data, variables) => {
      if (scope.merchantId) {
        // Include isCreator in queryKey to only update the active explorer tab
        let removed = 0;
        queryClient.setQueriesData<MerchantRecommendationsResponse>(
          { queryKey: ["merchant-recommendations", scope.merchantId, scope.workspaceId, isCreator] },
          (oldData) => {
            if (!oldData) return oldData;
            const next = filterMerchantRecommendation(oldData, variables.tiktokCreatorOpenId);
            removed = oldData.recommendations.length - next.recommendations.length;
            return next;
          },
        );
        queryClient.setQueriesData<RecommendationsCountResponse>(
          { queryKey: ["merchant-recommendations-count", scope.merchantId, scope.workspaceId, isCreator] },
          (oldData) => (oldData ? decrementCountCache(oldData, removed) : oldData),
        );
      }
    },
  });
};
